import { Popover } from '@chakra-ui/react';
import { IUsersInfo, UserStatusEnum } from '@monetize/types/app';
import { MouseEvent } from 'react';
import {
  MAvatar,
  MButton,
  MFlex,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { MemberAvatar } from '../../../../../components/Teams/MemberAvatar';
import { sortTeamUsers } from '../../../../../utils/settings';

const MAX_VISIBLE_MEMBERS = 8;

interface TeamMembersCellProps {
  users: IUsersInfo[];
  owner: string;
}

const stopRowClick = (event: MouseEvent) => event.stopPropagation();

/**
 * Avatar row with a "+N" chip that lists the rest. The owner is left out
 * entirely - the Owner column alongside already names them, and showing them
 * twice meant carrying a badge on the avatar to tell the two apart.
 */
export const TeamMembersCell = ({ users, owner }: TeamMembersCellProps) => {
  const members = sortTeamUsers(
    users.filter(
      ({ id, status }) => id !== owner && status !== UserStatusEnum.DEACTIVATED,
    ),
    owner,
  );
  const visibleMembers = members.slice(0, MAX_VISIBLE_MEMBERS);
  const hiddenCount = members.length - visibleMembers.length;

  if (!members.length) {
    return <MText color="fg.muted">Owner only</MText>;
  }

  return (
    <MFlex alignItems="center" flexWrap="nowrap">
      <MemberAvatar members={visibleMembers} owner={owner} />
      {hiddenCount > 0 && (
        <Popover.Root
          lazyMount
          unmountOnExit
          positioning={{ placement: 'bottom-start' }}
        >
          <Popover.Trigger asChild>
            <MButton
              variant="tertiary"
              size="xs"
              borderRadius="full"
              minW="25px"
              h="25px"
              px={1}
              onClick={stopRowClick}
              aria-label="Show all members"
            >
              +{hiddenCount}
            </MButton>
          </Popover.Trigger>
          <Popover.Positioner>
            <Popover.Content w="280px" onClick={stopRowClick}>
              <Popover.Arrow />
              <Popover.Body maxH="320px" overflowY="auto">
                <MStack gap={2}>
                  {members.map(({ id, name }) => (
                    <MFlex key={id} alignItems="center" gap={2}>
                      <MAvatar name={name} size="xs" />
                      <MText fontSize="sm" lineClamp={1}>
                        {name}
                      </MText>
                    </MFlex>
                  ))}
                </MStack>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Popover.Root>
      )}
    </MFlex>
  );
};
